import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { QAppVersionPolicyViolationsDataPoint } from '@kw/quokka-ui';

dayjs.extend(utc);

export const DASHBOARD_ANALYTICS_DATE_RANGES = [7, 14, 30, 60, 90] as const;

export type DashboardAnalyticsDateRangeDays = (typeof DASHBOARD_ANALYTICS_DATE_RANGES)[number];

export interface NewlySubmittedAppEntry {
  analysisId: string;
}

export interface AppTypeDistributionEntry {
  managementType: 'MANAGED' | 'REQUESTED' | 'UNMANAGED';
  versionCount: number;
}

export interface TopAppVersionEntry {
  applicationId: string;
  versionCount: number;
  appIconUrl?: string;
}

export interface TopAppCategoryEntry {
  category: string;
  uniqueAppsCount: number;
  versionCount: number;
}

export interface VendorRiskAssessmentEntry {
  vendor: string;
  noteCount: number;
  warningCount: number;
  errorCount: number;
}

export interface TopAppRiskEntry {
  riskName: string;
  versionCount: number;
}

export interface MostRiskAppEntry {
  appName: string;
  applicationId: string;
  vendor: string;
  versionCount: number;
  avgCount: number;
}

export interface MostErrorLevelRisksAppVersionEntry {
  appName: string;
  version: string;
  errorCount: number;
  appIconUrl?: string;
  analysisId: string;
}

export interface TopPolicyViolationEntry {
  policyName: string;
  violationCount: number;
}

export interface AppVersionWithMostPolicyViolationsEntry {
  appName: string;
  vendor: string;
  version: string;
  analysisId: string;
  violationCount: number;
}

export type CveSeverity = 'critical' | 'high' | 'medium' | 'low';

// SBOM "Risk Exposure by CVSS" bubble (combined platforms): one point per CVE scoring 7.0+.
export interface RiskExposureCvssEntry {
  cveId: string;
  cvssScore: number;
  maxConfidence: number | null;
  versionsCount: number;
  severity: CveSeverity;
}

// SBOM "App Versions with most Critical CVEs" table row (per platform).
export interface AppVersionCriticalCveEntry {
  appName: string;
  applicationId: string;
  vendor: string;
  version: string;
  analysisId: string;
  criticalCveCount: number;
}

// SBOM "Most Commonly found Critical CVEs" bar (per platform).
export interface CommonCriticalCveEntry {
  cveId: string;
  count: number;
}

export interface PolicyViolationsVersionCount {
  versionCount: number;
  androidVersionCount: number;
  iosVersionCount: number;
  uniqueAppsCount?: number;
  uniqueAndroidAppsCount?: number;
  uniqueIosAppsCount?: number;
  uniqueVersionCount?: number;
  uniqueAndroidVersionCount?: number;
  uniqueIosVersionCount?: number;
}

// Metrics row returned by GET /eval/analytics/metrics (subset used by dashboard cards).
export interface DashboardAnalyticsMetrics {
  id: string;
  createdAt: string;
  updatedAt?: string;
  orgId: string;
  dateRange: number;
  totalVersionsAll: number;
  totalVersionsAndroid: number;
  totalVersionsIos: number;
  uniqueAppsAll: number;
  uniqueAppsAndroid: number;
  uniqueAppsIos: number;
  newVersionsOverTime?: Array<{ date: string; total: number }>;
  newlySubmittedApps?: NewlySubmittedAppEntry[];
  appTypeDistribution?: AppTypeDistributionEntry[];
  topAppVersionsAndroid?: TopAppVersionEntry[];
  topAppVersionsIos?: TopAppVersionEntry[];
  topAppCategoriesAndroid?: TopAppCategoryEntry[];
  topAppCategoriesIos?: TopAppCategoryEntry[];
  unpassedErrorLevelRisksOverTime?: Array<{
    date: string;
    total: number;
    android: number;
    ios: number;
  }>;
  vendorRiskAssessmentAndroid?: VendorRiskAssessmentEntry[];
  vendorRiskAssessmentIos?: VendorRiskAssessmentEntry[];
  topAppRisksAndroid?: TopAppRiskEntry[];
  topAppRisksIos?: TopAppRiskEntry[];
  mostRiskAppsAndroid?: MostRiskAppEntry[];
  mostRiskAppsIos?: MostRiskAppEntry[];
  mostErrorLevelRisksAppVersionsAndroid?: MostErrorLevelRisksAppVersionEntry[];
  mostErrorLevelRisksAppVersionsIos?: MostErrorLevelRisksAppVersionEntry[];
  topPolicyViolationsAndroid?: TopPolicyViolationEntry[];
  topPolicyViolationsIos?: TopPolicyViolationEntry[];
  appVersionWithMostPolicyViolationsAndroid?: AppVersionWithMostPolicyViolationsEntry[];
  appVersionWithMostPolicyViolationsIos?: AppVersionWithMostPolicyViolationsEntry[];
  riskExposureByCvss?: RiskExposureCvssEntry[];
  appVersionsMostCriticalCvesAndroid?: AppVersionCriticalCveEntry[];
  appVersionsMostCriticalCvesIos?: AppVersionCriticalCveEntry[];
  mostCommonCriticalCvesAndroid?: CommonCriticalCveEntry[];
  mostCommonCriticalCvesIos?: CommonCriticalCveEntry[];
  policyViolationsVersionCount?: PolicyViolationsVersionCount;
  policyViolationsOverTime?: QAppVersionPolicyViolationsDataPoint[];
}

// Returns the drill-down date range as full UTC ISO timestamps so the app-list filter
// selects exactly the versions the dashboard cards counted. The cards are computed in the
// backend over whole UTC days (date_submitted::date BETWEEN (NOW() - N days)::date AND NOW()::date),
// so we anchor the range to UTC day boundaries: the start of the day N days ago (00:00:00.000)
// through the end of today (23:59:59.999)
export function rollingSubmittedDateRange(days: DashboardAnalyticsDateRangeDays): {
  startDate: string;
  endDate: string;
} {
  const startDate = dayjs.utc().subtract(days, 'day').startOf('day').toISOString();
  const endDate = dayjs.utc().endOf('day').toISOString();
  return { startDate, endDate };
}
